import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Chip,
  Table,
  Stack,
  Radio,
  Button,
  Dialog,
  Divider,
  MenuItem,
  TableRow,
  Container,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  RadioGroup,
  Typography,
  IconButton,
  FormLabel,
  DialogTitle,
  FormControl,
  Autocomplete,
  DialogContent,
  DialogActions,
  TableContainer,
  LinearProgress,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';

// ----------------------------------------------------------------------

type Audience = 'all' | 'user';
type NotifType =
  | 'announcement'
  | 'system'
  | 'payment'
  | 'quiz'
  | 'enrollment'
  | 'completion'
  // EPIC E7 widened NOTIFICATION_TYPES on the BE. Campaigns of this type are
  // normally written by the reminder sweep, not by hand, but the union has to
  // carry it or an existing 'reminder' row renders as an unknown type.
  | 'reminder';

type UserOption = { _id: string; name?: string; email?: string };

type Campaign = {
  batchId: string;
  title: string;
  title_ar?: string;
  body: string;
  body_ar?: string;
  type: NotifType;
  audience: Audience;
  link?: string;
  total: number;
  readCount: number;
  createdAt?: string;
};

type FormState = {
  batchId?: string; // present when editing
  audience: Audience;
  targetUser: UserOption | null;
  type: NotifType;
  title: string;
  title_ar: string;
  body: string;
  body_ar: string;
  link: string;
};

const EMPTY_FORM: FormState = {
  audience: 'all',
  targetUser: null,
  type: 'announcement',
  title: '',
  title_ar: '',
  body: '',
  body_ar: '',
  link: '',
};

const TYPE_OPTIONS: { value: NotifType; label: string }[] = [
  { value: 'announcement', label: 'Announcement' },
  { value: 'system', label: 'System' },
  { value: 'payment', label: 'Payment' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'enrollment', label: 'Enrollment' },
  { value: 'completion', label: 'Completion / Certificate' },
  { value: 'reminder', label: 'Class reminder' },
];

const fmtDate = (v?: string) => (v ? new Date(v).toLocaleString() : '—');

// ----------------------------------------------------------------------

export default function NotificationsList() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteState, setDeleteState] = useState<{ open: boolean; batchId?: string }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);
  // DELETE is admin-only on the backend; send/edit are open to customer-care
  // and sales.
  const canDelete = useIsFullAdmin();

  // Recipients viewer (who read / who hasn't) for one campaign.
  const [recipOpen, setRecipOpen] = useState(false);
  const [recipRows, setRecipRows] = useState<any[]>([]);
  const [recipLoading, setRecipLoading] = useState(false);
  const [recipMeta, setRecipMeta] = useState<{ title: string; total: number; readCount: number }>({
    title: '',
    total: 0,
    readCount: 0,
  });

  // user picker (async search)
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userInput, setUserInput] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.NOTIFICATIONS.LIST, {
        params: { page: 1, pageSize: 100 },
      });
      const data = res.data?.data ?? res.data;
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load notifications', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  // Debounced user search (only while the "specific user" target is active).
  useEffect(() => {
    if (form.audience !== 'user') return undefined;
    const handle = setTimeout(async () => {
      setUserLoading(true);
      try {
        const res = await axiosInstance.get(APIS.USERS.PAGINATED, {
          params: { page: 1, pageSize: 20, search: userInput || undefined },
        });
        const payload = res.data?.data ?? res.data;
        setUserOptions(Array.isArray(payload?.items) ? payload.items : []);
      } catch {
        setUserOptions([]);
      } finally {
        setUserLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [userInput, form.audience]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setUserInput('');
    setFormOpen(true);
  };
  const openEdit = (r: Campaign) => {
    setForm({
      batchId: r.batchId,
      audience: r.audience,
      targetUser: null,
      type: r.type,
      title: r.title || '',
      title_ar: r.title_ar || '',
      body: r.body || '',
      body_ar: r.body_ar || '',
      link: r.link || '',
    });
    setFormOpen(true);
  };
  const setField = (k: keyof FormState, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const openRecipients = async (r: Campaign) => {
    setRecipMeta({ title: r.title, total: r.total, readCount: r.readCount });
    setRecipRows([]);
    setRecipOpen(true);
    setRecipLoading(true);
    try {
      const res = await axiosInstance.get(APIS.NOTIFICATIONS.RECIPIENTS(r.batchId), {
        params: { page: 1, pageSize: 200 },
      });
      const data = res.data?.data ?? res.data;
      setRecipRows(Array.isArray(data?.items) ? data.items : []);
      setRecipMeta({
        title: r.title,
        total: data?.total ?? r.total,
        readCount: data?.readCount ?? r.readCount,
      });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load recipients', { variant: 'error' });
    } finally {
      setRecipLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      enqueueSnackbar('Title and message are required', { variant: 'warning' });
      return;
    }
    if (!form.batchId && form.audience === 'user' && !form.targetUser?._id) {
      enqueueSnackbar('Pick a user for a "specific user" notification', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      if (form.batchId) {
        // Edit content only (audience/target are fixed at send time).
        await axiosInstance.patch(APIS.NOTIFICATIONS.PATCH(form.batchId), {
          type: form.type,
          title: form.title.trim(),
          title_ar: form.title_ar.trim() || undefined,
          body: form.body.trim(),
          body_ar: form.body_ar.trim() || undefined,
          link: form.link.trim() || undefined,
        });
        enqueueSnackbar('Notification updated');
      } else {
        const res = await axiosInstance.post(APIS.NOTIFICATIONS.ADD, {
          audience: form.audience,
          ...(form.audience === 'user' ? { targetUserId: form.targetUser?._id } : {}),
          type: form.type,
          title: form.title.trim(),
          title_ar: form.title_ar.trim() || undefined,
          body: form.body.trim(),
          body_ar: form.body_ar.trim() || undefined,
          link: form.link.trim() || undefined,
        });
        const sent = (res.data?.data ?? res.data)?.recipients;
        enqueueSnackbar(
          `Notification sent${typeof sent === 'number' ? ` to ${sent} user${sent === 1 ? '' : 's'}` : ''}`,
        );
      }
      setFormOpen(false);
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.messages?.[0] || e?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.batchId) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(APIS.NOTIFICATIONS.DELETE(deleteState.batchId));
      enqueueSnackbar('Notification deleted');
      setDeleteState({ open: false });
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const isEditing = Boolean(form.batchId);

  return (
    <Page title="Notifications">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Notifications"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Notifications' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:paper-plane-fill" />}
              onClick={openNew}
            >
              New notification
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Send in-app notifications to <b>all users</b> or a <b>specific user</b>. Users see them in
            the bell menu on the site. Fill both English and Arabic so each user reads their language.
            Editing updates the message for everyone who received it; deleting removes it for all.
          </Typography>
        </Card>

        <Card>
          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
              />
            )}
            <Table sx={{ minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell align="center">Read</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      No notifications yet — click “New notification” to send one.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow
                    key={r.batchId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openRecipients(r)}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" noWrap>
                        {r.title}
                      </Typography>
                      {r.title_ar ? (
                        <Typography variant="caption" color="text.secondary" dir="rtl" display="block">
                          {r.title_ar}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Label color="default" sx={{ textTransform: 'capitalize' }}>
                        {r.type}
                      </Label>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={r.audience === 'all' ? 'info' : 'default'}
                        label={r.audience === 'all' ? 'All users' : 'Specific user'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Label color={r.readCount > 0 ? 'success' : 'default'}>
                        {r.readCount}/{r.total}
                      </Label>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.total}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(r.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        title="View recipients"
                        onClick={(e) => {
                          e.stopPropagation();
                          openRecipients(r);
                        }}
                      >
                        <Iconify icon="eva:people-fill" />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(r);
                        }}
                      >
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          sx={{ color: 'error.main' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteState({ open: true, batchId: r.batchId });
                          }}
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Create / edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit notification' : 'New notification'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* Audience */}
            <FormControl disabled={isEditing}>
              <FormLabel sx={{ mb: 0.5 }}>Send to</FormLabel>
              <RadioGroup
                row
                value={form.audience}
                onChange={(e) => setField('audience', e.target.value as Audience)}
              >
                <FormControlLabel value="all" control={<Radio />} label="All users" />
                <FormControlLabel value="user" control={<Radio />} label="Specific user" />
              </RadioGroup>
              {isEditing && (
                <Typography variant="caption" color="text.secondary">
                  Audience can’t be changed after sending — you can edit the content only.
                </Typography>
              )}
            </FormControl>

            {/* User picker */}
            {!isEditing && form.audience === 'user' && (
              <Autocomplete
                options={userOptions}
                loading={userLoading}
                value={form.targetUser}
                onChange={(_, v) => setField('targetUser', v)}
                onInputChange={(_, v) => setUserInput(v)}
                getOptionLabel={(o) => (o?.name ? `${o.name} (${o.email})` : o?.email || '')}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                filterOptions={(x) => x}
                noOptionsText={userInput ? 'No users found' : 'Type to search users'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target user"
                    placeholder="Search by name or email…"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <TextField
              select
              label="Type"
              value={form.type}
              onChange={(e) => setField('type', e.target.value as NotifType)}
              fullWidth
            >
              {TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <Divider textAlign="left">
              <Typography variant="overline" color="text.secondary">
                English
              </Typography>
            </Divider>
            <TextField
              label="Title (English)"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 160 }}
            />
            <TextField
              label="Message (English)"
              value={form.body}
              onChange={(e) => setField('body', e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              inputProps={{ maxLength: 1000 }}
            />

            <Divider textAlign="right">
              <Typography variant="overline" color="text.secondary">
                العربية
              </Typography>
            </Divider>
            <TextField
              label="Title (Arabic)"
              value={form.title_ar}
              onChange={(e) => setField('title_ar', e.target.value)}
              fullWidth
              inputProps={{ dir: 'rtl', maxLength: 160 }}
            />
            <TextField
              label="Message (Arabic)"
              value={form.body_ar}
              onChange={(e) => setField('body_ar', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              inputProps={{ dir: 'rtl', maxLength: 1000 }}
            />

            <TextField
              label="Link (optional)"
              value={form.link}
              onChange={(e) => setField('link', e.target.value)}
              fullWidth
              placeholder="/tracks"
              helperText="In-app path opened when the user clicks the notification (e.g. /tracks)."
            />

            <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
              <Typography variant="subtitle2">{form.title || 'Title preview'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {form.body || 'Message preview'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipients viewer — who received this notification and who has read it */}
      <Dialog open={recipOpen} onClose={() => setRecipOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          Recipients — {recipMeta.title}
          <Typography variant="body2" color="text.secondary">
            {recipMeta.readCount} of {recipMeta.total} read
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {recipLoading ? (
            <LinearProgress />
          ) : recipRows.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">
              No recipients found.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Read at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipRows.map((row: any, i: number) => {
                    const u = row.recipient || {};
                    const name =
                      u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—';
                    return (
                      <TableRow key={u._id || i} hover>
                        <TableCell>{name}</TableCell>
                        <TableCell>{u.email || '—'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            color={row.read ? 'success' : 'default'}
                            label={row.read ? 'Read' : 'Unread'}
                          />
                        </TableCell>
                        <TableCell>{row.read ? fmtDate(row.readAt) : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false })}
        confirmAction={handleDelete}
        headText="Delete notification"
        text="Delete this notification for everyone who received it? This can't be undone."
        loading={deleting}
      />
    </Page>
  );
}
